
import React, { useState } from 'react';
import { Modal, Form, Input, Upload, message, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import styled from 'styled-components';
import api from '../../services/api';

const { TextArea } = Input;

// Styled Components
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 1rem;
  }
`;

interface AddProbateDocumentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddProbateDocumentModal: React.FC<AddProbateDocumentModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  /**
   * Handles file selection in the Upload component.
   * @param param0 - The upload change parameters.
   */
  const onChangeUpload = ({ fileList }: UploadChangeParam<UploadFile>) => {
    console.log('File List Updated:', fileList); // Debugging log
    setFileList(fileList.slice(-1)); // Only keep the latest file
  };

  /**
   * Handles form submission to add a new probate document.
   */
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (fileList.length === 0) {
        message.error('Please select a file to upload.');
        return;
      }

      // Ensure the selected file exists
      if (!fileList[0].originFileObj) {
        message.error('Selected file is invalid.');
        return;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj as RcFile);
      formData.append('name', values.name);
      formData.append('description', values.description);

      // Make API call to upload the new probate document
      const response = await api.post('/probatedocs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('Probate document added successfully.');
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error: any) {
      console.error('Error adding probate document:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to add probate document.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title="Add New Probate Document"
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={uploading}
      okText="Add Document"
    >
      <StyledForm form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Document Title"
          rules={[{ required: true, message: 'Please enter the document title.' }]}
        >
          <Input placeholder="Enter document title" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Document Description"
          rules={[{ required: true, message: 'Please enter the document description.' }]}
        >
          <TextArea rows={4} placeholder="Enter document description" />
        </Form.Item>
        <Form.Item
          label="Upload Document" // Removed name="file"
        >
          <Upload
            beforeUpload={() => false} // Prevent automatic upload
            onChange={onChangeUpload}
            fileList={fileList}
            maxCount={1}
            accept=".pdf,.doc,.docx"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          <div style={{ marginTop: '0.5rem' }}>
            <span>Allowed file types: PDF, DOC, DOCX. Max size: 10MB.</span>
          </div>
        </Form.Item>
      </StyledForm>
    </Modal>
  );
};

export default AddProbateDocumentModal;
